import { useState, useRef, useCallback } from 'react'
import { RefreshCw, Upload, Check, X, Shuffle } from 'lucide-react'
import toast from 'react-hot-toast'
import Avatar, { AVATAR_STYLES } from './Avatar'
import { createLogger } from '../../utils/logger'

const logger = createLogger('AvatarEditor')

// Constants - extracted for maintainability
const MAX_UPLOAD_SIZE_KB = 500
const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_KB * 1024
const MAX_OPTIMIZED_SIZE_KB = 100
const MAX_OPTIMIZED_SIZE_BYTES = MAX_OPTIMIZED_SIZE_KB * 1024
const OPTIMIZED_IMAGE_MAX_SIZE = 256
const WEBP_COMPRESSION_QUALITY = 0.8

const AvatarEditor = ({
  currentType,
  currentSeed,
  currentStyle,
  currentData,
  name,
  publicName,
  onSave,
  onCancel
}) => {
  const [type, setType] = useState(currentType || 'dicebear')
  const [seed, setSeed] = useState(currentSeed || publicName)
  const [style, setStyle] = useState(currentStyle || 'bottts')
  const [customData, setCustomData] = useState(currentData)
  const [isUploading, setIsUploading] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)

  const fileInputRef = useRef(null)

  // Generate a new random seed
  const handleRandomize = () => {
    setSeed(`${publicName}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`)
  }

  // Quick randomize: change both seed and style randomly
  const handleQuickRandomize = () => {
    const styles = Object.keys(AVATAR_STYLES)
    const randomStyle = styles[Math.floor(Math.random() * styles.length)]
    setStyle(randomStyle)
    setSeed(`${publicName}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`)
  }

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type (GIF added for compatibility, but will be converted to WebP)
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, WebP, or GIF image')
      return
    }

    // Validate size before optimization
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      toast.error(`Image must be less than ${MAX_UPLOAD_SIZE_KB}KB`)
      return
    }

    setIsUploading(true)

    const reader = new FileReader()

    // Handle FileReader errors
    reader.onerror = () => {
      logger.error('FileReader error:', reader.error)
      toast.error('Failed to read image file')
      setIsUploading(false)
    }

    reader.onload = (event) => {
      const img = new Image()

      // Handle image load errors (corrupted/invalid image)
      img.onerror = () => {
        logger.error('Image load error: invalid or corrupted image')
        toast.error('Invalid or corrupted image file')
        setIsUploading(false)
      }

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')

          // Validate canvas context
          if (!ctx) {
            throw new Error('Canvas context unavailable')
          }

          // Resize to max dimensions while maintaining aspect ratio
          let { width, height } = img
          if (width > height) {
            if (width > OPTIMIZED_IMAGE_MAX_SIZE) {
              height = Math.round((height * OPTIMIZED_IMAGE_MAX_SIZE) / width)
              width = OPTIMIZED_IMAGE_MAX_SIZE
            }
          } else {
            if (height > OPTIMIZED_IMAGE_MAX_SIZE) {
              width = Math.round((width * OPTIMIZED_IMAGE_MAX_SIZE) / height)
              height = OPTIMIZED_IMAGE_MAX_SIZE
            }
          }

          canvas.width = width
          canvas.height = height
          ctx.drawImage(img, 0, 0, width, height)

          // Convert to WebP for smaller size
          const dataUrl = canvas.toDataURL('image/webp', WEBP_COMPRESSION_QUALITY)

          // Check final size
          const base64Size = Math.ceil((dataUrl.length * 3) / 4)
          if (base64Size > MAX_OPTIMIZED_SIZE_BYTES) {
            toast.error('Optimized image still too large. Try a smaller image.')
            setIsUploading(false)
            return
          }

          setCustomData(dataUrl)
          setType('custom')
          setIsUploading(false)
          toast.success('Image uploaded successfully')
        } catch (error) {
          logger.error('Canvas processing error:', error)
          toast.error('Failed to process image')
          setIsUploading(false)
        }
      }

      img.src = event.target.result
    }

    reader.readAsDataURL(file)
  }, [])

  const handleSave = () => {
    onSave({
      avatarType: type,
      avatarSeed: type === 'dicebear' ? seed : null,
      avatarStyle: type === 'dicebear' ? style : null,
      avatarData: type === 'custom' ? customData : null
    })
  }

  return (
    <div className="space-y-6">
      {/* Preview */}
      <div className="flex justify-center">
        <Avatar
          type={type}
          seed={seed}
          style={style}
          data={customData}
          name={name}
          publicName={publicName}
          size="xl"
          className="ring-4 ring-gray-100 dark:ring-gray-700"
        />
      </div>

      {/* Type Selection */}
      <div className="flex gap-2 justify-center flex-wrap">
        <button
          type="button"
          onClick={() => setType('initials')}
          className={`px-4 py-2 rounded-lg border transition-colors ${
            type === 'initials'
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
              : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          Initials
        </button>
        <button
          type="button"
          onClick={() => {
            setType('dicebear')
            if (!seed) setSeed(publicName || 'avatar')
          }}
          className={`px-4 py-2 rounded-lg border transition-colors ${
            type === 'dicebear'
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
              : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          Generated
        </button>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className={`px-4 py-2 rounded-lg border flex items-center gap-2 transition-colors ${
            type === 'custom'
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
              : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
          } disabled:opacity-50`}
        >
          <Upload className="w-4 h-4" />
          {isUploading ? 'Uploading...' : 'Upload'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileUpload}
          className="hidden"
        />
      </div>

      {/* DiceBear Options */}
      {type === 'dicebear' && (
        <div className="space-y-4">
          {/* Quick Randomize Button */}
          <button
            type="button"
            onClick={handleQuickRandomize}
            className="flex items-center gap-2 w-full justify-center px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-colors font-medium"
          >
            <Shuffle className="w-5 h-5" />
            Surprise Me!
          </button>

          {/* Style Selection */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Avatar Style
            </label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(AVATAR_STYLES).map(([key, { name }]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setStyle(key)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    style === key
                      ? 'bg-primary-500 text-white ring-2 ring-primary-300'
                      : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>

          {/* Randomize Current Style */}
          <button
            type="button"
            onClick={handleRandomize}
            className="flex items-center gap-2 w-full justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
          >
            <RefreshCw className="w-4 h-4" />
            Regenerate This Style
          </button>

          {/* Advanced Options Toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline w-full text-center"
          >
            {showAdvanced ? 'Hide' : 'Show'} Advanced Options
          </button>

          {/* Advanced Options */}
          {showAdvanced && (
            <div className="space-y-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              {/* Custom Seed Input */}
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
                  Custom Seed (avatar identifier)
                </label>
                <input
                  type="text"
                  value={seed || ''}
                  onChange={(e) => setSeed(e.target.value)}
                  placeholder="Enter any text..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  The same seed always generates the same avatar
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
        >
          <X className="w-4 h-4" />
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
        >
          <Check className="w-4 h-4" />
          Save Avatar
        </button>
      </div>
    </div>
  )
}

export default AvatarEditor
